// frontend/src/components/paywall/StatusCard.tsx

import React from 'react';
import { UsageSummary, PaywallModalVariant } from '../../types/paywall';

/**
 * Status Card Props
 */
export interface StatusCardProps {
  variant: PaywallModalVariant;
  usage: UsageSummary;
  trialDaysRemaining: number | null;
}

/**
 * Status messages for each paywall variant
 */
interface StatusMessage {
  badge: string;
  title: string;
  description: string;
  statusLabel: string;
  statusValue: string;
}

/**
 * Status Card Component
 * Displays user's current usage status with variant-specific messaging
 */
export const StatusCard: React.FC<StatusCardProps> = ({ variant, usage, trialDaysRemaining }) => {
  /**
   * Get status message based on variant
   */
  const getStatusMessage = (): StatusMessage => {
    switch (variant) {
      case 'no-credits-trial-active':
        return {
          badge: 'FREE TRIAL USER',
          title: 'AI Helps Exhausted',
          description: `You have 0 AI helps remaining out of ${usage.totalCredits} free credits. Your trial is still active for ${trialDaysRemaining} more ${
            trialDaysRemaining === 1 ? 'day' : 'days'
          }, but you'll need to upgrade to continue using AI features.`,
          statusLabel: 'Trial period:',
          statusValue: `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} remaining`,
        };

      case 'trial-expired-with-credits':
        return {
          badge: 'FREE TRIAL USER',
          title: 'Your Trial Has Ended',
          description: `Your 10-day trial period has expired. You still have ${usage.creditsRemaining} AI helps remaining, but trial credits cannot be used after expiration. Upgrade to Pro to continue learning.`,
          statusLabel: 'Trial period:',
          statusValue: 'Expired',
        };

      case 'both-expired':
        return {
          badge: 'FREE TRIAL USER',
          title: 'Your Trial Has Ended',
          description: `You have 0 AI helps remaining. Upgrade to Pro to continue learning with unlimited AI assistance.`,
          statusLabel: 'Trial period:',
          statusValue: 'Expired',
        };

      case 'pro-credits-exhausted':
        return {
          badge: 'PRO USER',
          title: 'Monthly Credits Exhausted',
          description: `You have 0 AI helps remaining out of ${usage.totalCredits.toLocaleString()} monthly credits. Your credits will reset automatically at the start of your next billing cycle.`,
          statusLabel: 'Credits reset:',
          statusValue: usage.currentPeriodEndsAt
            ? `In ${Math.ceil((new Date(usage.currentPeriodEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days`
            : 'Soon',
        };

      default:
        return {
          badge: 'FREE TRIAL USER',
          title: 'Your Trial Has Ended',
          description: `Upgrade to Pro to continue learning with AI assistance.`,
          statusLabel: 'Trial period:',
          statusValue: 'Expired',
        };
    }
  };

  const message = getStatusMessage();
  const creditsUsed = usage.totalCredits - usage.creditsRemaining;
  const percentageUsed = (creditsUsed / usage.totalCredits) * 100;

  return (
    <div className="status-card">
      <span className="status-badge">{message.badge}</span>
      <h2 id="paywall-title" className="status-title">{message.title}</h2>
      <p id="paywall-description" className="status-description">{message.description}</p>

      <div className="usage-display">
        <div className="usage-label">AI Helps Remaining</div>
        <div className="usage-numbers">
          {usage.creditsRemaining.toLocaleString()} / {usage.totalCredits.toLocaleString()}
        </div>
        <div className="usage-bar">
          <div
            className="usage-bar-fill"
            style={{ width: `${percentageUsed}%` }}
            aria-valuenow={percentageUsed}
            aria-valuemin={0}
            aria-valuemax={100}
            role="progressbar"
          />
        </div>
        <div className="usage-status">
          {message.statusLabel} <span className="status-value">{message.statusValue}</span>
        </div>
      </div>
    </div>
  );
};
